import { useState, useEffect, useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import {
  BookOpen,
  Award,
  CalendarDays,
  GraduationCap,
  Clock,
  MapPin,
  ChevronRight,
  Sparkles,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/ui/card'
import { Badge } from '@/ui/badge'
import { useAuthStore } from '@/core/store/authStore'
import { useIsMounted } from '@/core/hooks/useIsMounted'
import { useClassSessionStore } from '@/features/classes/stores'
import { getAllSessions } from '@/features/classes/api/sessionsService'
import { getAllCertificates } from '@/features/classes/api/certificatesService'
import type { ClassSession, Certificate } from '@/features/classes/stores/types'

interface StudentSession {
  session: ClassSession
  courseName: string
  attendeeStatus: string
}

export function StudentOverview() {
  const mounted = useIsMounted()
  const user = useAuthStore((s) => s.user)
  const { courses, loadCourses, loadMyAttendees } = useClassSessionStore()

  const [sessions, setSessions] = useState<ClassSession[]>([])
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [loading, setLoading] = useState(true)

  const userEmail = user?.email?.toLowerCase() ?? ''

  useEffect(() => {
    void (async () => {
      if (!mounted.current) {return}
      loadCourses()
      const [sess, certs] = await Promise.all([
        getAllSessions().catch(() => []),
        getAllCertificates().catch(() => []),
      ])
      if (!mounted.current) {return}
      setSessions(sess)
      setCertificates(certs)
      await loadMyAttendees()
      if (!mounted.current) {return}
      setLoading(false)
    })()
  }, [loadCourses, loadMyAttendees])

  // Filter sessions where this student is registered
  const mySessions = useMemo(() => {
    const result: StudentSession[] = []
    for (const session of sessions) {
      // Check if user email matches any attendee for this session
      // Attendees are loaded separately; we need to check via the store
      const store = useClassSessionStore.getState()
      const sessionAttendees = store.attendees.filter((a) => a.sessionId === session.id)
      const myAttendee = sessionAttendees.find((a) => a.studentId.toLowerCase() === userEmail)
      if (myAttendee) {
        const course = courses.find((c) => c.slug === session.courseId)
        result.push({
          session,
          courseName: course?.name ?? session.courseName ?? session.courseId,
          attendeeStatus: myAttendee.status,
        })
      }
    }
    return result.sort((a, b) => a.session.date.localeCompare(b.session.date))
  }, [sessions, courses, userEmail])

  // My certificates
  const myCertificates = useMemo(() => {
    // Map certificates to sessions to find course info
    return certificates
      .map((cert) => {
        const store = useClassSessionStore.getState()
        const attendee = store.attendees.find((a) => a.id === cert.attendeeId)
        if (attendee?.studentId.toLowerCase() !== userEmail) {return null}
        const session = sessions.find((s) => s.id === attendee.sessionId)
        const course = courses.find((c) => c.slug === session?.courseId)
        return {
          ...cert,
          courseName: course?.name ?? session?.courseName ?? session?.courseId ?? 'Unknown Course',
          isExpired: new Date(cert.expiresAt) < new Date(),
          isRevoked: cert.revoked,
        }
      })
      .filter(Boolean) as Array<Certificate & { courseName: string; isExpired: boolean; isRevoked: boolean }>
  }, [certificates, sessions, courses, userEmail])

  const validCerts = myCertificates.filter((c) => !c.isExpired && !c.isRevoked)

  // Available courses (not yet registered)
  const registeredCourseIds = useMemo(
    () => new Set<string>(mySessions.map((s) => s.session.courseId)),
    [mySessions]
  )
  const availableCourses = courses.filter((c) => c.isActive !== false && !registeredCourseIds.has(c.slug))

  // Upcoming sessions (public, not registered yet)
  const upcomingPublicSessions = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10)
    return sessions
      .filter((s) => s.date >= today && !registeredCourseIds.has(s.courseId))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 5)
  }, [sessions, registeredCourseIds])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Welcome header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary-deep flex items-center justify-center shadow-sm">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-navy">
            Welcome back, {user?.name?.split(' ')[0] ?? 'Student'}!
          </h1>
          <p className="text-sm text-slate-500">
            Here's your learning dashboard. Continue where you left off.
          </p>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">My Courses</p>
                <p className="text-2xl font-bold text-navy">{mySessions.length}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Certificates</p>
                <p className="text-2xl font-bold text-navy">{validCerts.length}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                <Award className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Upcoming</p>
                <p className="text-2xl font-bold text-navy">
                  {mySessions.filter((s) => s.session.date >= new Date().toISOString().slice(0, 10)).length}
                </p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Completed</p>
                <p className="text-2xl font-bold text-navy">
                  {mySessions.filter((s) => s.attendeeStatus === 'completed' || s.attendeeStatus === 'quiz-completed').length}
                </p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* My sessions / progress */}
        <div className="lg:col-span-2 space-y-5">
          {/* Active / upcoming sessions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg font-semibold text-navy">My Training Sessions</CardTitle>
                </div>
                <Link
                  to="/courses"
                  className="text-xs font-medium text-primary hover:underline flex items-center gap-0.5"
                >
                  Browse courses <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {mySessions.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">You haven't registered for any courses yet.</p>
                  <Link
                    to="/courses"
                    className="inline-block mt-2 text-sm font-medium text-primary hover:underline"
                  >
                    Explore available courses
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {mySessions.map(({ session, courseName, attendeeStatus }) => {
                    const isCompleted = attendeeStatus === 'completed' || attendeeStatus === 'quiz-completed'
                    const isUpcoming = session.date >= new Date().toISOString().slice(0, 10)
                    return (
                      <div key={session.id} className="px-4 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isCompleted ? 'bg-green-50' : isUpcoming ? 'bg-blue-50' : 'bg-orange-50'}`}>
                            {isCompleted ? (
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                            ) : (
                              <Clock className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-navy">{courseName}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <CalendarDays className="w-3 h-3" />
                                {session.date}
                              </span>
                              {session.location && (
                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {session.location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Badge
                          variant="default"
                          className={
                            isCompleted
                              ? 'bg-green-50 text-green-700 border border-green-100'
                              : isUpcoming
                                ? 'bg-blue-50 text-blue-700 border border-blue-100'
                                : 'bg-orange-50 text-primary border border-orange-100'
                          }
                        >
                          {isCompleted ? 'Completed' : isUpcoming ? 'Upcoming' : 'In Progress'}
                        </Badge>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Certificates */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-semibold text-navy">My Certificates</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {myCertificates.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <Award className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No certificates yet. Complete a course to earn one!</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {myCertificates.map((cert) => (
                    <div key={cert.id} className="px-4 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cert.isRevoked ? 'bg-red-50' : cert.isExpired ? 'bg-yellow-50' : 'bg-green-50'}`}>
                          <Award className={`w-5 h-5 ${cert.isRevoked ? 'text-red-500' : cert.isExpired ? 'text-yellow-600' : 'text-green-600'}`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy">{cert.courseName}</p>
                          <p className="text-xs text-slate-500">
                            {cert.certificateNumber} · Expires {cert.expiresAt}
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant="default"
                        className={
                          cert.isRevoked
                            ? 'bg-red-50 text-red-700 border border-red-100'
                            : cert.isExpired
                              ? 'bg-yellow-50 text-yellow-700 border border-yellow-100'
                              : 'bg-green-50 text-green-700 border border-green-100'
                        }
                      >
                        {cert.isRevoked ? 'Revoked' : cert.isExpired ? 'Expired' : 'Valid'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-5">
          {/* Available courses */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg font-semibold text-navy">Available Courses</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              {availableCourses.slice(0, 6).map((course) => (
                <Link
                  key={course.id}
                  to="/courses"
                  className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-md bg-navy flex items-center justify-center shrink-0">
                      <GraduationCap className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-navy group-hover:text-primary transition-colors">
                        {course.name}
                      </p>
                      <p className="text-xs text-slate-500">Valid {course.validYears}y</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-primary transition-colors" />
                </Link>
              ))}
              {availableCourses.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-2">You're enrolled in all available courses!</p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming public sessions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-semibold text-navy">Upcoming Sessions</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2.5 pt-0">
              {upcomingPublicSessions.length === 0 ? (
                <div className="text-center py-3">
                  <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-1.5" />
                  <p className="text-sm text-slate-500">No upcoming sessions scheduled.</p>
                </div>
              ) : (
                upcomingPublicSessions.map((session) => {
                  const course = courses.find((c) => c.slug === session.courseId)
                  return (
                    <div key={session.id} className="flex items-start gap-2.5 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                        <CalendarDays className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-navy truncate">{course?.name ?? session.courseId}</p>
                        <p className="text-xs text-slate-500">{session.date} · {session.location}</p>
                      </div>
                    </div>
                  )
                })
              )}
            </CardContent>
          </Card>

          {/* Quick tip */}
          <div className="bg-gradient-to-br from-navy to-navy-light rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <p className="text-sm font-semibold">Tip</p>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Keep your certifications up to date. Most safety courses need to be renewed every 3 years. Check your certificate expiry dates regularly.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
